import { useEffect, useState } from "react";
import { listItems, api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowUpRight, Bookmark, Trash2 } from "lucide-react";
import { toast } from "sonner";
import LibraryItemDialog from "@/components/LibraryItemDialog";

const KIND_META = {
  offer: { label: "Oferta", tag: "tag-pill-mint" },
  client: { label: "Cliente", tag: "tag-pill-amber" },
  proposal: { label: "Propuesta", tag: "tag-pill-rose" },
};

export default function Library() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    try {
      const r = await listItems();
      setItems(r);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { refresh(); }, []);

  const openItem = (it) => { setSelected(it); setOpen(true); };
  const closeItem = () => { setOpen(false); setTimeout(() => setSelected(null), 200); };

  const deleteItem = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este elemento? No se podrá recuperar.")) return;
    try {
      await api.delete(`/library/${id}`);
      toast.success("Eliminado");
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      console.error(err);
      toast.error("No se pudo eliminar");
    }
  };

  const filtered = (kind) => (kind === "all" ? items : items.filter((i) => i.kind === kind));

  return (
    <div className="space-y-10">
      <header className="relative">
        <div className="blob blob-lavender w-[200px] h-[200px] -top-6 left-10" />
        <div className="blob blob-mint w-[200px] h-[200px] top-0 right-1/4" style={{ animationDelay: "2s" }} />
        <div className="relative">
          <span className="tag-pill tag-pill-lavender mb-5"><Bookmark className="w-3 h-3" /> Biblioteca</span>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-[-0.04em] leading-[0.95]">
            Tu <span className="scribble-underline">memoria</span><br />
            estratégica.
          </h1>
          <p className="text-base text-foreground/65 mt-5 max-w-2xl leading-relaxed">
            Todo lo que has generado: ofertas, análisis de clientes y propuestas. Haz clic en cualquier tarjeta para ver el resultado completo.
          </p>
        </div>
      </header>

      <Tabs defaultValue="all">
        <TabsList className="bg-white border border-black/[0.08] rounded-2xl p-1 grid grid-cols-2 md:grid-cols-4 h-auto" data-testid="library-tabs">
          <TabsTrigger value="all" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">Todo</TabsTrigger>
          <TabsTrigger value="offer" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">Ofertas</TabsTrigger>
          <TabsTrigger value="client" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">Clientes</TabsTrigger>
          <TabsTrigger value="proposal" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">Propuestas</TabsTrigger>
        </TabsList>
        {["all", "offer", "client", "proposal"].map((k) => (
          <TabsContent key={k} value={k} className="mt-6">
            {filtered(k).length === 0 ? (
              <div className="app-card p-12 text-center relative">
                <div className="blob blob-rose w-[260px] h-[260px] top-0 left-1/2 -translate-x-1/2 opacity-40" />
                <p className="text-sm text-foreground/65 relative">Aún no hay nada guardado aquí.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered(k).map((it) => {
                  const meta = KIND_META[it.kind] || { label: it.kind, tag: "" };
                  return (
                    <button
                      type="button"
                      key={it.id}
                      onClick={() => openItem(it)}
                      data-testid={`library-item-${it.id}`}
                      className="app-card p-5 min-h-[160px] flex flex-col justify-between group cursor-pointer text-left hover:border-foreground/30 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <span className={`tag-pill ${meta.tag}`}>{meta.label}</span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => deleteItem(e, it.id)}
                            data-testid={`library-item-delete-${it.id}`}
                            className="w-7 h-7 rounded-full grid place-items-center text-foreground/40 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                            aria-label="Eliminar"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <ArrowUpRight className="w-4 h-4 text-foreground/30 group-hover:text-foreground group-hover:rotate-12 transition-all" strokeWidth={2} />
                        </div>
                      </div>
                      <div>
                        <div className="font-display font-black tracking-[-0.01em] text-base line-clamp-2">{it.title}</div>
                        <div className="text-[10px] text-foreground/55 mt-2">{new Date(it.created_at).toLocaleString()}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <LibraryItemDialog item={selected} open={open} onClose={closeItem} />
    </div>
  );
}
